/*
 * 地址码表缓存（同步移动端 src/libs/useCachedAddressJson.js）
 * 同一个产品会按 addressCodeType 取到不同的码表，所以缓存需要按 productId + addressCodeType 区分，
 * 并用 pending 队列避免同一份数据重复请求。
 */
import { getAreaCode, getAllAreaCode } from '@/api/api';

// 地址码表类型
export const ENUM_ADDRESS_CODE_TYPE = {
    'FILTER_DENIED_COVERAGE': 'FILTER_DENIED_COVERAGE', // 使用产品专属码表，但过滤拒保地区
    'HIGHLIGHT_DENIED_COVERAGE': 'HIGHLIGHT_DENIED_COVERAGE', // 使用产品专属码表，展示拒保地区但不可选
    'ALL': 'ALL', // 使用产品专属码表，全部地区（拒保地区也可选，不做标识）
    'GENERAL': 'GENERAL', // 使用通用码表，全部地区
};

// 缓存对象
const areaCodeCache = {};
// 进行中的请求
const pendingRequests = {};
// 缓存过期时间（10分钟）
const CACHE_EXPIRATION_TIME = 10 * 60 * 1000;

function getAddressJsonCacheKey(productId, addressType) {
    if (addressType === ENUM_ADDRESS_CODE_TYPE.GENERAL || productId == null) {
        return 'areaCode_general';
    }
    return `areaCode_${productId}_${addressType}`;
}

/**
 * 带缓存的地址码表
 * @param {string} productId - 产品ID，为空时取通用码表
 * @param {string} addressType - ENUM_ADDRESS_CODE_TYPE
 * @returns {Promise<{province: Array, city: Array, area: Array}>}
 */
export const getCachedAddressJson = async (productId, addressType = ENUM_ADDRESS_CODE_TYPE.FILTER_DENIED_COVERAGE) => {
    const effectiveAddressType = productId == null ? ENUM_ADDRESS_CODE_TYPE.GENERAL : addressType;
    const cacheKey = getAddressJsonCacheKey(productId, effectiveAddressType);
    const cachedData = areaCodeCache[cacheKey];

    if (cachedData && Date.now() - cachedData.timestamp < CACHE_EXPIRATION_TIME) {
        return cachedData.data;
    }

    // 同一份数据已有请求在进行中，复用该请求
    if (pendingRequests[cacheKey]) {
        return pendingRequests[cacheKey];
    }

    const requestPromise = (async () => {
        try {
            let result = {};
            if (effectiveAddressType === ENUM_ADDRESS_CODE_TYPE.GENERAL) {
                result = await getAreaCode();
            } else if (effectiveAddressType === ENUM_ADDRESS_CODE_TYPE.FILTER_DENIED_COVERAGE) {
                result = await getAreaCode(productId);
            } else {
                result = await getAllAreaCode(productId);
            }
            // JYYF-8149, 接口异常时只会返回空数据，不写入缓存，避免空码表被缓存10分钟
            if (result && result.province && result.province.length) {
                areaCodeCache[cacheKey] = {
                    data: result,
                    timestamp: Date.now(),
                };
            }
            return result;
        } catch (error) {
            console.error('获取区域编码失败:', error);
            throw error;
        } finally {
            // 请求完成后，无论成功失败都删除 pending 状态
            delete pendingRequests[cacheKey];
        }
    })();

    pendingRequests[cacheKey] = requestPromise;

    return requestPromise;
};



// WEBPACK FOOTER //
// ./src/libs/useCachedAddressJson.js